import React, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import {
  Button,
  Grid,
  Typography,
  Snackbar,
  CircularProgress,
  Link,
  FormControlLabel,
  Checkbox,
  useTheme,
  Box,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { LOGIN } from '@shared/helpers/routes';
import {
  getProvinces,
  getTaxSituations,
  getEntityTypes,
} from '@shared/services/services';
import authService from '@shared/services/authService';
import { useSelector } from 'react-redux';

import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import DoneIcon from '@mui/icons-material/Done';

import { sxStyled } from './styles';
import StyledTextField from '@shared/components/StyledTextField';
import GenericDropdown from '@shared/components/GenericDropdown';
import FormSection from '@shared/components/FormSection';

const initialValues = {
  agency: {
    name: '',
    business_name: '',
    file: '',
    tax_situation: '',
    province: '',
    city: '',
    cp: '',
    address: '',
    phone: '',
    email: '',
    whatsapp: '',
    type: '',
    cuit: '',
  },
  user: {
    email: '',
    name: '',
    lastname: '',
    whatsapp: '',
  },
  certificate: '',
};

const Register = () => {
  const theme = useTheme();

  const sxStyle = sxStyled(theme);

  const pageConfiguration = useSelector(store => store.pageConfiguration);
  const [provinces, setProvinces] = useState([]);
  const [taxSituations, setTaxSituations] = useState([]);
  const [types, setTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState({
    message: '',
    isVisible: false,
    type: '',
  });
  const [check, setCheck] = useState({
    legajo: true,
    afip: false,
  });

  const validationSchema = Yup.object().shape({
    agency: Yup.object().shape({
      name: Yup.string().required('Nombre requerido'),
      business_name: Yup.string().required(
        'Razón social / nombre propietario requerido'
      ),
      file: check.legajo
        ? Yup.string().required('Campo requerido')
        : Yup.string(),
      cuit: Yup.string().required('Cuit requerido'),
      tax_situation: Yup.string().required('Situación fiscal requerida'),
      type: Yup.string().required('Tipo requerido'),
      province: Yup.string().required('Provincia requerida'),
      city: Yup.string().required('Ciudad requerida'),
      cp: Yup.string().required('Código postal requerido'),
      address: Yup.string().required('Dirección requerida'),
      phone: Yup.string().required('Teléfono requerido'),
      email: Yup.string()
        .email('Formato inválido de email')
        .required('Email requerido'),
      whatsapp: Yup.string().required('Whatsapp requerido'),
    }),
    user: Yup.object().shape({
      name: Yup.string().required('Nombre requerido'),
      lastname: Yup.string().required('Apellido requerido'),
      email: Yup.string()
        .email('Formato inválido de email')
        .required('Email requerido'),
      whatsapp: Yup.string().required('Whatsapp requerido'),
    }),
    certificate: check.afip
      ? Yup.mixed().required('Campo requerido')
      : Yup.mixed(),
  });

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async values => {
      setIsLoading(true);
      handleSubmit(values);
    },
  });

  useEffect(() => {
    getProvincesData();
    getTaxSituationsData();
    getTypesData();
  }, []);

  const handleSubmit = values => {
    authService
      .registerEntity(values)
      .then(() => {
        handleOpenAlert('Entidad registrada con éxito!', 'success');
        setIsLoading(false);
        formik.resetForm();
      })
      .catch(err => {
        console.error('register error', err);
        setIsLoading(false);
        handleOpenAlert('Error al registrar la entidad', 'error');
      });
  };

  const getProvincesData = () => {
    getProvinces()
      .then(res => setProvinces(res))
      .catch(err => console.error('PROVINCES ERROR', err));
  };

  const getTaxSituationsData = () => {
    getTaxSituations()
      .then(res => setTaxSituations(res))
      .catch(err => console.error('TAX SITUATIONS ERROR', err));
  };

  const getTypesData = () => {
    getEntityTypes()
      .then(res => setTypes(res))
      .catch(err => console.error('TYPES ERROR', err));
  };

  const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
  });

  const handleOpenAlert = (message, type) => {
    setOpenAlert({
      message,
      isVisible: true,
      type,
    });
  };

  const handleCheckout = event => {
    if (event.target.name == 'certificate') {
      formik.setFieldValue('agency.afip', '');
    }

    if (event.target.name == 'legajo') {
      formik.setFieldValue('certificate', '');
    }

    setCheck({
      legajo: !check.legajo,
      afip: !check.afip,
    });
  };

  const handleCloseAlert = () => {
    setOpenAlert({
      message: '',
      isVisible: false,
      type: '',
    });
  };

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      sx={sxStyle.root}
    >
      <Grid
        item
        container
        justifyContent="space-between"
        alignItems="center"
        sx={sxStyle.header}
      >
        <Grid item container direction="column" xs={10}>
          <Grid item>
            <Typography variant="h5" color="inherit" sx={sxStyle.title}>
              Datos de Acceso
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              variant="subtitle1"
              color="inherit"
              sx={sxStyle.subtitle}
            >
              Para poder operar con GIT Operador de Turismo, debe completar la
              planilla a continuación.
            </Typography>
          </Grid>
        </Grid>
        <Grid item xs>
          <img
            src={pageConfiguration.site_logos.principal}
            style={{
              width: '220px',
            }}
          />
        </Grid>
      </Grid>
      <form onSubmit={formik.handleSubmit}>
        {/* Datos de Agencia */}
        <FormSection
          title="Datos de Agencia"
          padding="16px"
          customLayout={{
            xs: {
              1: 12,
              2: 12,
              3: 12,
              4: 12,
              5: 12,
              6: 12,
              7: 12,
              8: 12,
              9: 12,
              10: 12,
              11: 12,
              12: 12,
              13: 12,
              14: 12,
              15: 12,
            },
            md: {
              1: 6,
              2: 6,
              3: 1,
              4: 3,
              5: 4,
              6: 4,
              7: 6,
              8: 6,
              9: 6,
              10: 6,
              11: 6,
              12: 6,
              13: 4,
              14: 4,
              15: 4,
            },
          }}
        >
          <StyledTextField
            label="Nombre agencia"
            name="agency.name"
            formik={formik}
          />
          <StyledTextField
            label="Razon Social / Nombre Propietario"
            name="agency.business_name"
            formik={formik}
          />

          <FormControlLabel
            control={
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon />}
                checkedIcon={
                  <DoneIcon style={{ color: 'rgb(170, 230, 230)' }} />
                }
                checked={check.legajo}
              />
            }
            name="legajo"
            value={check.legajo}
            onChange={handleCheckout}
            label="Legajo"
          />
          <FormControlLabel
            control={
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon />}
                checkedIcon={
                  <DoneIcon style={{ color: 'rgb(170, 230, 230)' }} />
                }
                checked={check.afip}
              />
            }
            name="afip"
            value={check.afip}
            onChange={handleCheckout}
            label="Constancia de inscripción AFIP"
          />

          {check.legajo ? (
            <StyledTextField
              key="legajo"
              label="Legajo"
              name="agency.file"
              formik={formik}
            />
          ) : (
            <StyledTextField
              key="certificate"
              label="Constancia de inscripción AFIP"
              name="certificate"
              formik={formik}
              type="file"
              onChange={event =>
                formik.setFieldValue(
                  'certificate',
                  event.target.files?.[0] ?? null
                )
              }
            />
          )}

          <StyledTextField label="CUIT" name="agency.cuit" formik={formik} />

          <GenericDropdown
            items={provinces.map(p => ({
              id: p.id,
              fragments: [{ text: p.name }],
            }))}
            selectedItem={
              formik.values.agency.province
                ? { fragments: [{ text: formik.values.agency.province }] }
                : null
            }
            onSelect={item =>
              formik.setFieldValue('agency.province', item.fragments[0].text)
            }
            buttonLabel="Seleccioná una provincia"
            isPrimaryBorder
            hasArrow
            hasError={
              formik.touched.agency?.province &&
              Boolean(formik.errors.agency?.province)
            }
            helperText={
              formik.touched.agency?.province && formik.errors.agency?.province
            }
          />

          <StyledTextField label="Ciudad" name="agency.city" formik={formik} />
          <StyledTextField
            label="Dirección"
            name="agency.address"
            formik={formik}
          />
          <StyledTextField
            label="Código Postal"
            name="agency.cp"
            formik={formik}
          />

          <GenericDropdown
            items={taxSituations.map(tax => ({
              id: tax.id,
              fragments: [{ text: tax.name }],
            }))}
            selectedItem={
              formik.values.agency.tax_situation
                ? {
                    fragments: [{ text: formik.values.agency.tax_situation }],
                  }
                : null
            }
            onSelect={item =>
              formik.setFieldValue(
                'agency.tax_situation',
                item.fragments[0].text
              )
            }
            buttonLabel="Situación Fiscal"
            isPrimaryBorder
            hasArrow
            hasError={
              formik.touched.agency?.tax_situation &&
              Boolean(formik.errors.agency?.tax_situation)
            }
            helperText={
              formik.touched.agency?.tax_situation &&
              formik.errors.agency?.tax_situation
            }
          />

          <GenericDropdown
            items={types.map(type => ({
              id: type.id,
              fragments: [{ text: type.name }],
            }))}
            selectedItem={
              formik.values.agency.type
                ? { fragments: [{ text: formik.values.agency.type }] }
                : null
            }
            onSelect={item =>
              formik.setFieldValue('agency.type', item.fragments[0].text)
            }
            buttonLabel="Tipo"
            isPrimaryBorder
            hasArrow
            hasError={
              formik.touched.agency?.type && Boolean(formik.errors.agency?.type)
            }
            helperText={
              formik.touched.agency?.type && formik.errors.agency?.type
            }
          />

          <StyledTextField
            label="Email Agencia"
            name="agency.email"
            formik={formik}
          />
          <StyledTextField
            label="Teléfono"
            name="agency.phone"
            formik={formik}
          />
          <StyledTextField
            label="Whatsapp para Promoción"
            name="agency.whatsapp"
            formik={formik}
          />
        </FormSection>

        {/* Datos Vendedor */}
        <FormSection
          title="Datos Vendedor"
          padding="16px"
          spacing={2}
          mdVal={6}
        >
          <StyledTextField label="Nombre" name="user.name" formik={formik} />
          <StyledTextField
            label="Apellido"
            name="user.lastname"
            formik={formik}
          />
          <StyledTextField label="Email" name="user.email" formik={formik} />
          <StyledTextField
            label="Whatsapp para Promoción"
            name="user.whatsapp"
            formik={formik}
          />
        </FormSection>

        {/* Botones y info */}
        <Box
          sx={{
            my: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2, // espacio entre items
          }}
        >
          <Typography
            variant="subtitle2"
            align="center"
            sx={{ mx: 'auto' }} // centra y limita ancho
          >
            Una vez verificados los datos ingresados se enviará por mail su
            clave de acceso.
          </Typography>

          <Button
            color="primary"
            variant="contained"
            type="submit"
            size="large"
            sx={{
              width: { xs: '100%', sm: '300px' },
              fontWeight: 600,
              color: theme.text1,
              '&:hover': {
                bgcolor: theme.primary,
                opacity: 0.9,
              },
            }}
          >
            {isLoading ? (
              <CircularProgress size={25} sx={{ color: theme.text1 }} />
            ) : (
              'Enviar'
            )}
          </Button>

          <Link href={LOGIN} sx={{ textDecoration: 'none' }}>
            <Typography variant="subtitle2" align="center">
              Ya tienes una cuenta? Inicia Sesión
            </Typography>
          </Link>
        </Box>
      </form>
      <Snackbar
        open={openAlert.isVisible}
        autoHideDuration={3000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity={openAlert.type}>
          {openAlert.message ?? ''}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default Register;
